import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../config/api";
import { useTranslate } from "../../Hook/useTranslate";

const EyeOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const WaseenyLogo = () => (
  <svg width="230" height="84" viewBox="0 0 230 84" xmlns="http://www.w3.org/2000/svg">
    <line x1="2" y1="37" x2="14" y2="37" stroke="#E31E24" strokeWidth="2.8" strokeLinecap="round" />
    <line x1="4" y1="42" x2="14" y2="42" stroke="#E31E24" strokeWidth="2" strokeLinecap="round" />
    <line x1="6" y1="32" x2="14" y2="32" stroke="#E31E24" strokeWidth="2" strokeLinecap="round" />
    <ellipse cx="40" cy="26" rx="24" ry="18" fill="#E31E24" />
    <rect x="16" y="26" width="48" height="6" rx="1" fill="#E31E24" />
    <text x="40" y="17" textAnchor="middle" fill="white" fontSize="5.5" fontWeight="700" fontFamily="Arial">100% HALAL</text>
    <ellipse cx="40" cy="19" rx="14" ry="7" fill="white" opacity="0.12" />
    <rect x="13" y="32" width="55" height="18" rx="4" fill="#111111" />
    <line x1="19" y1="37" x2="62" y2="37" stroke="#2a2a2a" strokeWidth="1.2" />
    <line x1="19" y1="41" x2="62" y2="41" stroke="#2a2a2a" strokeWidth="1.2" />
    <rect x="66" y="22" width="4.5" height="24" rx="2.2" fill="#111111" />
    <circle cx="26" cy="54" r="8" fill="#111" stroke="#555" strokeWidth="1.8" />
    <circle cx="26" cy="54" r="4" fill="#2a2a2a" />
    <circle cx="26" cy="54" r="1.5" fill="#555" />
    <circle cx="55" cy="54" r="8" fill="#111" stroke="#555" strokeWidth="1.8" />
    <circle cx="55" cy="54" r="4" fill="#2a2a2a" />
    <circle cx="55" cy="54" r="1.5" fill="#555" />
    <text x="228" y="30" textAnchor="end" fill="#111" fontSize="17" fontWeight="700" fontFamily="'Arial', sans-serif">وصيني</text>
    <text x="84" y="60" fill="#E31E24" fontSize="32" fontWeight="900" fontFamily="'Georgia','Times New Roman',serif" letterSpacing="-1">W</text>
    <text x="107" y="60" fill="#111111" fontSize="32" fontWeight="900" fontFamily="'Georgia','Times New Roman',serif" letterSpacing="-1">aseeny</text>
    <text x="84" y="74" fill="#888" fontSize="7.8" fontFamily="Arial" letterSpacing="2.2">||| DELIVERY AT HOME |||</text>
  </svg>
);

export default function ForgotPassword() {
  const navigate = useNavigate();
  const t = useTranslate();
  const [step, setStep] = useState("email"); // email, otp, newPassword, success
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);
  const [timer, setTimer] = useState(0);
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer(t => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleStepOne = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!email.trim()) {
      setError(t("enterEmailError"));
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setMessage("OTP sent to your registered mobile number");
      setStep("otp");
      setTimer(120); // 2 minutes
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError("");
    setMessage("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/resend-otp", { email });
      setMessage("OTP resent successfully");
      setTimer(120);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleStepTwo = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!otp.trim()) {
      setError(t("enterOtpError"));
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password/verify-otp", { email, otp });
      setResetToken(res.data.resetToken); // Store the reset token from backend
      setMessage("OTP verified successfully");
      setStep("newPassword");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleStepThree = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!newPassword.trim()) {
      setError(t("enterNewPassError"));
      return;
    }

    if (newPassword.length < 6) {
      setError(t("passLengthError"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("passMatchError"));
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        resetToken,
        newPassword,
      });
      setMessage("Password reset successfully!");
      setStep("success");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    page: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      overflow: "hidden",
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
    },
    bgImage: {
      position: "absolute",
      inset: 0,
      backgroundImage: `url('https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1600&q=80&auto=format&fit=crop')`,
      backgroundSize: "cover",
      backgroundPosition: "center top",
      filter: "brightness(1.05) saturate(0.9)",
      zIndex: 0,
    },
    bgOverlay: {
      position: "absolute",
      inset: 0,
      background: "radial-gradient(ellipse 90% 90% at 50% 48%, rgba(255,253,248,0.86) 0%, rgba(255,251,244,0.76) 38%, rgba(255,248,238,0.50) 70%, rgba(255,245,230,0.22) 100%)",
      zIndex: 1,
    },
    card: {
      position: "relative",
      zIndex: 2,
      width: "100%",
      maxWidth: "610px",
      padding: "0 48px 52px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    logoWrap: {
      marginBottom: "8px",
      display: "flex",
      justifyContent: "center",
    },
    heading: {
      fontSize: "38px",
      fontWeight: "800",
      color: "#111827",
      margin: "0 0 8px",
      textAlign: "center",
      letterSpacing: "-0.5px",
    },
    subheading: {
      fontSize: "15.5px",
      color: "#888",
      margin: "0 0 34px",
      textAlign: "center",
      fontWeight: "400",
    },
    form: {
      width: "100%",
    },
    fieldGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      fontSize: "15px",
      fontWeight: "600",
      color: "#1a1a1a",
      marginBottom: "8px",
    },
    input: {
      width: "100%",
      padding: "16px 20px",
      fontSize: "15.5px",
      color: "#374151",
      backgroundColor: "rgba(255,255,255,0.96)",
      border: "1.5px solid #e2e8f0",
      borderRadius: "12px",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    button: {
      width: "100%",
      padding: "18px",
      marginTop: "28px",
      background: "linear-gradient(135deg, #E31E24 0%, #C8181E 100%)",
      color: "#fff",
      fontSize: "17px",
      fontWeight: "700",
      border: "none",
      borderRadius: "50px",
      cursor: "pointer",
      transition: "transform 0.15s, box-shadow 0.15s",
      boxShadow: "0 4px 18px rgba(227,30,36,0.4)",
    },
    backButton: {
      background: "none",
      border: "none",
      color: "#E31E24",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      marginTop: "20px",
      textDecoration: "none",
    },
    alert: {
      padding: "12px 16px",
      borderRadius: "8px",
      marginBottom: "16px",
      fontSize: "14px",
      fontWeight: "500",
    },
    errorAlert: {
      background: "#FEE",
      color: "#c91a1a",
      border: "1px solid #FBCAC8",
    },
    successAlert: {
      background: "#EFE",
      color: "#1a7a1a",
      border: "1px solid #D0F0D0",
    },
    passwordWrapper: {
      position: "relative",
    },
    eyeButton: {
      position: "absolute",
      right: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#b0b0b0",
    },
    resendButton: {
      background: "none",
      border: "none",
      color: "#E31E24",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      marginTop: "12px",
      textDecoration: "underline",
    },
    centerText: {
      textAlign: "center",
      fontSize: "14px",
      color: "#666",
      marginTop: "16px",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.bgImage} />
      <div style={styles.bgOverlay} />

      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <WaseenyLogo />
        </div>

        <h1 style={styles.heading}>
          {step === "email" && "Reset Password"}
          {step === "otp" && "Verify OTP"}
          {step === "newPassword" && "New Password"}
          {step === "success" && "Success!"}
        </h1>

        <p style={styles.subheading}>
          {step === "email" && "Enter your email to reset your password"}
          {step === "otp" && "Enter OTP sent to your mobile"}
          {step === "newPassword" && "Enter your new password"}
          {step === "success" && "Your password has been reset"}
        </p>

        {error && (
          <div style={{ ...styles.alert, ...styles.errorAlert }}>
            {error}
          </div>
        )}

        {message && (
          <div style={{ ...styles.alert, ...styles.successAlert }}>
            {message}
          </div>
        )}

        <div style={styles.form}>
          {/* STEP 1: EMAIL */}
          {step === "email" && (
            <form onSubmit={handleStepOne}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>{t("emailLabel")}</label>
                <input
                  type="email"
                  style={styles.input}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("enterEmailPlaceholder")}
                  required
                />
              </div>
              <button
                type="submit"
                style={styles.button}
                disabled={loading}
                onMouseEnter={(e) => (e.target.style.transform = "translateY(-2px)")}
                onMouseLeave={(e) => (e.target.style.transform = "translateY(0)")}
              >
                {loading ? t("sending") : t("sendOtp")}
              </button>
            </form>
          )}

          {/* STEP 2: OTP */}
          {step === "otp" && (
            <form onSubmit={handleStepTwo}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>{t("enterOtpLabel")}</label>
                <input
                  type="text"
                  style={styles.input}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  required
                />
              </div>
              <button
                type="submit"
                style={styles.button}
                disabled={loading}
                onMouseEnter={(e) => (e.target.style.transform = "translateY(-2px)")}
                onMouseLeave={(e) => (e.target.style.transform = "translateY(0)")}
              >
                {loading ? t("verifying") : t("verifyOtp")}
              </button>
              <div style={styles.centerText}>
                {timer > 0 ? (
                  `${t("resendOtpIn")} ${timer}s`
                ) : (
                  <button
                    type="button"
                    style={styles.resendButton}
                    onClick={handleResendOTP}
                    disabled={loading}
                  >
                    {t("resendOtpBtn")}
                  </button>
                )}
              </div>
            </form>
          )}

          {/* STEP 3: NEW PASSWORD */}
          {step === "newPassword" && (
            <form onSubmit={handleStepThree}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>{t("newPassword")}</label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showNewPass ? "text" : "password"}
                    style={{ ...styles.input, paddingRight: "52px" }}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t("newPasswordPlaceholder")}
                    required
                  />
                  <button
                    type="button"
                    style={styles.eyeButton}
                    onClick={() => setShowNewPass(!showNewPass)}
                  >
                    {showNewPass ? <EyeIcon /> : <EyeOffIcon />}
                  </button>
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>{t("confirmPasswordLabel")}</label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showConfirmPass ? "text" : "password"}
                    style={{ ...styles.input, paddingRight: "52px" }}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t("confirmNewPasswordPlaceholder")}
                    required
                  />
                  <button
                    type="button"
                    style={styles.eyeButton}
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                  >
                    {showConfirmPass ? <EyeIcon /> : <EyeOffIcon />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                style={styles.button}
                disabled={loading}
                onMouseEnter={(e) => (e.target.style.transform = "translateY(-2px)")}
                onMouseLeave={(e) => (e.target.style.transform = "translateY(0)")}
              >
                {loading ? t("resetting") : t("resetPassword")}
              </button>
            </form>
          )}

          {/* STEP 4: SUCCESS */}
          {step === "success" && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "48px", marginBottom: "20px" }}>✓</div>
              <p style={{ color: "#1a7a1a", marginBottom: "28px", fontSize: "16px" }}>
                {t("passwordResetSuccessMsg")}
              </p>
              <button
                style={styles.button}
                onClick={() => navigate("/login")}
                onMouseEnter={(e) => (e.target.style.transform = "translateY(-2px)")}
                onMouseLeave={(e) => (e.target.style.transform = "translateY(0)")}
              >
                {t("backToLogin")}
              </button>
            </div>
          )}
        </div>

        {step !== "success" && (
          <button
            style={styles.backButton}
            onClick={() => {
              if (step === "email") {
                navigate("/login");
              } else {
                setStep("email");
                setEmail("");
                setOtp("");
                setError("");
                setMessage("");
              }
            }}
          >
            ← {t("back")}
          </button>
        )}
      </div>
    </div>
  );
}
